import { commonClassName } from 'components/pages/PromoScorecard';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { CustomSelect } from '../BumpReport/MainSection';
import DownSvg from 'assets/svg/Down';
import {
    barWidth,
    legendData,
    salesGraphWidth,
} from 'components/organism/GrowthAnalysis/SalesChartContainer/SalesChartContainer';
import Legend from 'components/molecules/Legend';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { chartHeight, yAxisWidth } from './BarPointChartContainer';
import Barchart from 'components/molecules/Barchart';
import {
    formatNumWithKMBAlt,
    getAdditionalDataOnCopyClick,
    getConfig,
    scaleMaxValue,
    scaleMinValue,
} from 'utils/helperMethods';
import { ppgVsYaChartData } from 'utils/testData';
import { graphXAxisLabelFontSize } from 'components/organism/GrowthAnalysis/SalesAttributionDrilldownChart';
import { yAxisLabelClass } from '../GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useFetchPriceLadderChart2 } from 'hooks/priceLadder/useFetchPriceLadderChart2';
import _ from 'lodash';
import Loader from 'components/molecules/Loader';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import HorizontalInfiniteScroller from 'components/molecules/HorizontalInfiniteScroller';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { priceLadderChart2Download } from 'parser/graphToExcelDownloadParser';
import { trackEventMixpanel } from 'utils/mixpanel';
import { PRICE_LADDER_CHART2_DROPDOWN_CHANGED } from 'utils/mixpanelEventNames';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { ConfigKeys } from 'utils/Enums';

const mainLabel = 'Price point change vs ';

export const barChartContainerId = 'price-ladder-bar-chart-container';

interface Props {
    selectPPG: any;
    subTileState: any;
    widthConfig: any;
    selectedKpi: any;
    setSelectedKpi: Function;
    priceLadderChart2Error: any;
    priceLadderChart2Data: any;
    priceLadderChart2Loading: boolean;
    getPriceLadderData: Function;
    offset: number;
    infiniteScrollWidthRef: any;
    isMarket: boolean;
    selectedAttributeItem: string;
}

const BarChartContainer: FC<Props> = ({
    selectPPG,
    subTileState,
    widthConfig,
    selectedKpi,
    setSelectedKpi,
    priceLadderChart2Error,
    priceLadderChart2Data,
    priceLadderChart2Loading,
    getPriceLadderData,
    offset,
    infiniteScrollWidthRef,
    isMarket,
    selectedAttributeItem,
}) => {
    //config
    const { appConfig } = useContext(UserContext);
    const priceLadderConfig = getConfig('priceLadder', appConfig);
    const { decimalForPercentage, decimalForNonPercentage } = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);

    const graphRef = useRef<any>();
    const { allDimAndAttributeTypeList, allCheckedAttribute, timeSelectionLabel } = useContext(FilterContextData);

    //width config
    const { containerWidth, graphViewContainerWidth, perGraphComponentWidth } = widthConfig;

    //kpi options
    const memoisedKpi = useMemo(() => {
        const kpis = priceLadderConfig?.legendMapping?.[subTileState.key]?.slice(1)?.map((item: any) => {
            return {
                label: item?.label,
                value: item?.label,
                key: item?.dataKey,
                metricKey: item?.metricDataKey,
            };
        });
        return kpis;
    }, [subTileState]);

    useEffect(() => {
        if (subTileState) {
            const kpis = priceLadderConfig?.legendMapping?.[subTileState.key]?.slice(1)?.[0];
            const tempKpi = {
                label: kpis?.label,
                value: kpis?.label,
                key: kpis?.dataKey,
                metricKey: kpis?.metricDataKey,
            };
            setSelectedKpi(tempKpi);
        }
    }, [subTileState]);

    const combinedLoading = allDimAndAttributeTypeList?.loading || priceLadderChart2Loading;

    function infiniteScrollOnNext(width: number, loading: boolean) {
        if (loading) return;
        //  console.log('logging asd', width, loading);
        infiniteScrollWidthRef.current = width - 200;
        // getPriceLadderData([1, 2], offset + priceLadderConfig?.limit);
    }

    const excelData = useMemo(() => {
        return priceLadderChart2Download(priceLadderChart2Data?.chartData?.data, selectPPG?.label, selectedKpi?.label);
    }, [subTileState, selectedKpi, selectPPG, priceLadderChart2Data]);

    return (
        <div ref={graphRef} className={`${commonClassName} mt-4`}>
            <div className="flex justify-between items-center gap-4">
                <div className="flex justify-between items-center gap-4">
                    <p className="font-lg text-headerColor font-medium">
                        {mainLabel}{' '}
                        {priceLadderConfig?.tileData?.length === 1
                            ? selectedAttributeItem
                            : isMarket
                            ? selectedKpi?.label
                            : priceLadderChart2Data?.chartData?.data?.[0]?.category}
                    </p>
                    <div className="flex justify-between items-center">
                        <h5 className="font-medium font-xs m-0 !text-fontGray mr-3">KPI</h5>
                        <CustomSelect
                            className="dropdown-height dropdown-width-alt uniqSelPricePointKPIDropDown"
                            suffixIcon={<DownSvg />}
                            value={selectedKpi?.value}
                            disabled={combinedLoading}
                            options={memoisedKpi}
                            onChange={(val: any, item: any) => {
                                setSelectedKpi(item);
                                //mixpanel event
                                trackEventMixpanel(PRICE_LADDER_CHART2_DROPDOWN_CHANGED, {
                                    kpiName: item?.label,
                                });
                            }}
                        />
                    </div>
                </div>

                {!combinedLoading && (
                    <div className="flex items-center gap-2">
                        <CopyToClipBoard refOfComponent={graphRef} />

                        <DownloadExcel
                            title={'PriceLadderChart2'}
                            excelData={excelData?.data}
                            headers={excelData?.headers}
                        />
                    </div>
                )}
            </div>
            {combinedLoading ? (
                <div style={{ height: chartHeight + 50 }}>
                    <Loader />
                </div>
            ) : priceLadderChart2Error || !priceLadderChart2Data ? (
                <div
                    style={{
                        height: chartHeight + 50,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <ErrorBox msg={String(priceLadderChart2Error || 'No Data')} />
                </div>
            ) : (
                <>
                    <div className="flex justify-between items-center my-4">
                        <p className={`${yAxisLabelClass} w-[120px]`}>{selectedKpi?.label}</p>
                        <Legend data={legendData} />
                        <p className="w-[120px]"></p>
                    </div>

                    <div className="flex">
                        {/* Y1 AXIS */}
                        <Barchart
                            // padding={[0, 0, 0, 48]}
                            padding={[0, 0, -2, 0]}
                            barWidth={30}
                            xAxisConfig={false}
                            dataset={[]}
                            height={chartHeight}
                            loading={false}
                            chartContainer={`price-ladder-bar-y1`}
                            fields={['category', 'sales']}
                            //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                            minBound={scaleMinValue(30, priceLadderChart2Data?.chartData?.minVal)}
                            maxBound={scaleMaxValue(10, priceLadderChart2Data?.chartData?.maxVal)}
                            width={yAxisWidth}
                            colorKey={'color'}
                            yAxisLabelFormatter={(val: any) => {
                                return formatNumWithKMBAlt(
                                    Number(val) + priceLadderChart2Data?.chartData?.weightedAvg,
                                    decimalForNonPercentage,
                                    decimalForNonPercentage,
                                );
                            }}
                            // Yline
                            YGrid={false}
                        />
                        <HorizontalInfiniteScroller
                            id={barChartContainerId}
                            hasMore={false} //subtracting 1 to remove total value which is the first item
                            scrollThreshold={0.9}
                            toScrollWidth={infiniteScrollWidthRef.current}
                            onNext={(width: number) => {
                                infiniteScrollOnNext(width, combinedLoading);
                            }}
                        >
                            <div
                                style={{ marginRight: yAxisWidth }}
                                id={barChartContainerId}
                                className="overflow-x-auto w-full pb-[40px]"
                            >
                                <div className="flex">
                                    <Barchart
                                        tooltipFormatter={(xAxis: any, val: any) => {
                                            return {
                                                name: xAxis,
                                                value: `${formatNumWithKMBAlt(val, decimalForPercentage)}%`,
                                            };
                                        }}
                                        intervalFormatter={(val: any) => {
                                            return `${formatNumWithKMBAlt(val, decimalForPercentage)}%`;
                                        }}
                                        // padding={[0, 0, 0, 48]}
                                        padding={[0, 0, 0, 0]}
                                        barWidth={barWidth}
                                        dataset={priceLadderChart2Data?.chartData?.data}
                                        height={chartHeight}
                                        loading={false}
                                        chartContainer={`price-ladder-bar-main`}
                                        fields={['category', 'sales']}
                                        //   minBound={scaleMinValue(40, tileChartData?.lineMaxVal)}
                                        minBound={scaleMinValue(30, priceLadderChart2Data?.chartData?.minVal)}
                                        maxBound={scaleMaxValue(10, priceLadderChart2Data?.chartData?.maxVal)}
                                        width={graphViewContainerWidth}
                                        colorKey={'color'}
                                        // Yline
                                        // YGrid={false}
                                    />
                                </div>
                                <div className="flex">
                                    {/* rest of data label */}
                                    {priceLadderChart2Data?.chartData?.data?.map((item: any, idx: number) => {
                                        return (
                                            <div
                                                style={{
                                                    width: perGraphComponentWidth,
                                                    minWidth: perGraphComponentWidth,
                                                    overflowWrap: 'anywhere',
                                                    fontSize: graphXAxisLabelFontSize,
                                                }}
                                                className="text-center p-[2px]"
                                                key={item?.category}
                                            >
                                                {item?.category}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </HorizontalInfiniteScroller>
                    </div>
                    <p className={`text-center !top-[-35px] mb-[-40px] relative ${yAxisLabelClass}`}>
                        {selectPPG?.label}
                    </p>
                </>
            )}
        </div>
    );
};

export default BarChartContainer;
